import React, { Component } from 'react';
import styled from 'styled-components';
import { Editor } from 'slate-react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import { SAFARI } from '@utility/enums/browsers';
import { fnBrowserDetect } from '@utility/browsers';
import { clearGlobalSelection } from '@utility/clearGlobalSelection';
import { Tab, Tabs } from '@heroui/react';
import Schema from '../EditorSection/Schema';
import {
  INSERT_TEXT,
  ADD_MARK,
  REMOVE_MARK,
  SET_SELECTION,
} from '../../../../../../../../constants/selectionOpts';
import renderBlock from '../EditorSection/renderBlock';
import Actions from '../../../../../../../../store/actions';
import paste from '../EditorSection/utils/paste';
import keyDown from '../EditorSection/utils/keyDown';
import renderMark from '../../../../../editJournal/slateUtilities/renderMark';
import stopCommentExtension from '../../../../../../../../utility/commentExtension';
import CommentsCard from '../../../../../../../comments/commentsCard';
import resumePlugins from '../EditorSection/resumePlugins';
import { FormatTypes } from '../../../../../../../../constants/templateDictionaries';
import KEY_CODES from '../../../../../editJournal/constants/keyCodes';
import SnackBarAlerts from '../../../../../../../common/snackBarAlerts/snackBarAlerts';
import breakpointsMinWidth from '../../../../../../../../styling/breakpointsMinWidth';
import { TABS } from './linksTabsEnums';

const PersonalHeader = styled.div`
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  @media ${breakpointsMinWidth.tabletSm} {
    margin-bottom: 12px;
  }
  @media ${breakpointsMinWidth.desktopSm} {
    margin-bottom: 16px;
  }
  @media ${breakpointsMinWidth.desktopMd} {
    margin-bottom: 20px;
  }
`;

const PersonalHeaderText = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #000000;
  @media ${breakpointsMinWidth.desktopMd} {
    font-size: 14px;
  }
`;

const PersonalHeaderIcon = styled.div`
  display: flex;
  align-items: center;
`;

const PersonalLinkWrapper = styled.div`
  cursor: default;
  grid-column: 1/-1;

  width: -webkit-fill-available;
  width: -moz-available;
  height: fit-content;
  width: 100%;
  text-align: left;
`;

const PersonalLinkEditor = styled.div`
  position: relative;
`;

class PersonalLinks extends Component {
  constructor(props) {
    super(props);

    const { personalLinks } = props;

    this.state = {
      value: personalLinks,
      showSnackBar: false,
      snackBarMessage: null,
      snackBarType: null,
    };
  }

  componentDidMount = () => {
    // this.editorRef.focus();
    const {
      setInputEditorRef,
      setMarkType,
      componentKey,
      userPreferences,
      formatType,
      showIcons,
      updateUserPreferences,
    } = this.props;

    setInputEditorRef(componentKey, this.editorRef);
    // Marks Setting it back to default
    setMarkType({
      bold: false,
      italic: false,
      underline: false,
    });
    const { editorRefs } = this.props;
    if (editorRefs) {
      const {
        value: {
          focusBlock,
          selection: { isFocused },
        },
      } = editorRefs[componentKey];
      const selections = editorRefs[componentKey];
      if (selections && focusBlock && isFocused) {
        selections?.deselect()?.blur();
      }
    }

    updateUserPreferences({
      userPreferences: {
        ...userPreferences,
        showIcons: { ...userPreferences.showIcons, ...showIcons },
      },
      formatType,
    });
  };

  componentDidUpdate = (prevProps) => {
    const {
      personalLinks: { document: previousDocument },
    } = prevProps;

    const { personalLinks: incomingSection } = this.props;
    const { document: currentDocument } = incomingSection;

    if (previousDocument !== currentDocument) {
      this.setState({ value: incomingSection });
    }
  };

  componentWillUnmount = () => {
    if (fnBrowserDetect() === SAFARI) {
      clearGlobalSelection();
    }
  };

  onChange = ({ value, operations }) => {
    const { document: currentDocument } = value;

    const {
      personalLinks: { document: previousDocument },
      updateSection,
    } = this.props;

    const operationsArray = operations.toArray();

    let isSelectionOps = true;

    operationsArray.forEach((oper) => {
      if (
        oper.type !== SET_SELECTION &&
        !(oper.type === ADD_MARK && oper.mark.type === 'fakeSelection') &&
        !(oper.type === REMOVE_MARK && oper.mark.type === 'fakeSelection')
      )
        isSelectionOps = false;
    });

    /**
     * Debounce only when user types rest of the events
     * dispatch immediately
     */
    const debounce = operationsArray.some(
      (operation) => operation.type === INSERT_TEXT,
    );

    if (currentDocument !== previousDocument && !isSelectionOps) {
      clearTimeout(this.timer);
      if (debounce) {
        this.timer = setTimeout(
          () =>
            updateSection({
              updateType: 'component',
              key: 'personalLinks',
              sectionKey: 'personalInfo',
              data: { value },
            }),
          500,
        );
      } else {
        updateSection({
          updateType: 'component',
          key: 'personalLinks',
          sectionKey: 'personalInfo',
          data: { value },
        });
      }
    }
    this.setState({ value });
  };

  onKeyDown = (event, editor, next) => {
    const {
      updatePopUpKeys,
      showCommentCard,
      showContextCard,
      personalLinks,
      formatType,
      activePlan,
      subscriptionStatus,
      editorRefs,
    } = this.props;
    stopCommentExtension({
      editor: this.editorRef,
      showCommentCard,
      showContextCard,
      event,
      updatePopUpKeys,
    });
    const isPreventDefault = keyDown({
      personalLinks,
      event,
      editor,
      formatType,
      activePlan,
      subscriptionStatus,
      editorRefs,
      updatePopUpKeys,
      showCommentCard,
      showContextCard,
    });
    if (isPreventDefault) return;

    const keyCodeSet = new Set([KEY_CODES.B, KEY_CODES.I, KEY_CODES.U]);

    if ((event.ctrlKey || event.metaKey) && keyCodeSet.has(event.which)) return;

    next();
  };

  onPaste = (event, editor, next) => {
    const { formatType } = this.props;
    const isPreventDefault = paste({
      event,
      editor,
      formatType,
      setSnackBar: this.setSnackBar,
    });
    if (isPreventDefault) return;
    next();
  };

  renderMark = (...args) => {
    const argumentsData = args;
    const { formatType } = this.props;
    argumentsData[0].formatType = formatType;
    return renderMark(...argumentsData);
  };

  onFocus = (event, editor, next) => {
    const { setInputEditorRef, componentKey } = this.props;
    setInputEditorRef(componentKey);
    next();
  };

  onBlur = (event, editor, next) => {
    const { setInputEditorRef } = this.props;
    setInputEditorRef();
    next();
  };

  renderBlock = (props, editor, next) => {
    const { formatType, statusBarRef, mostUsedToolSetRef } = this.props;
    return renderBlock(
      props,
      editor,
      next,
      formatType,
      null,
      statusBarRef,
      mostUsedToolSetRef,
    );
  };

  handleClose = () => this.setState({ showSnackBar: false });

  setSnackBar = ({ snackBarMessage, snackBarType }) => {
    const { showSnackBar } = this.state;
    this.setState({
      snackBarMessage,
      snackBarType,
      showSnackBar: !showSnackBar,
    });
  };

  handleSelectChange = (value) => {
    const { updateUserPreferences, userPreferences, formatType } = this.props;

    const updatedUserPreferences = {
      ...userPreferences,
      covid19vaccinated: value,
    };

    updateUserPreferences({
      userPreferences: updatedUserPreferences,
      formatType,
    });
  };

  handleRating = (checked) => {
    const {
      updateUserPreferences,
      userPreferences,
      formatType,
      componentKey,
      showIcons,
    } = this.props;

    updateUserPreferences({
      userPreferences: {
        ...userPreferences,
        showIcons: { ...showIcons, [componentKey]: checked },
      },
      formatType,
    });
  };

  handleTabChange = (tab) => {
    const {
      userPreferences,
      updateUserPreferences,
      formatType,
      componentKey,
      showIcons,
      showLinks,
      showTexts,
    } = this.props;

    const newShowIcons = { ...showIcons, [componentKey]: false };
    const newShowLinks = { ...showLinks, [componentKey]: false };
    const newShowTexts = { ...showTexts, [componentKey]: false };

    if (tab === TABS.ICON) {
      newShowIcons[componentKey] = true;
    } else if (tab === TABS.LINK) {
      newShowLinks[componentKey] = true;
    } else if (tab === TABS.TEXT) {
      newShowTexts[componentKey] = true;
    }

    updateUserPreferences({
      userPreferences: {
        ...userPreferences,
        showIcons: newShowIcons,
        showLinks: newShowLinks,
        showTexts: newShowTexts,
      },
      formatType,
    });
  };

  getDefaultSelectedTab() {
    const { showIcons, showLinks, showTexts, componentKey } = this.props;
    if (showIcons?.[componentKey]) {
      return TABS.ICON;
    }
    if (showLinks?.[componentKey]) {
      return TABS.LINK;
    }
    if (showTexts?.[componentKey]) {
      return TABS.TEXT;
    }
    return TABS.LINK;
  }

  render() {
    const { value, showSnackBar, snackBarMessage, snackBarType } = this.state;
    const {
      showCommentCard,
      showContextCard,
      permission,
      saveDocument,
      formatType,
    } = this.props;
    let focusBlockType;
    const { focusBlock } = value;
    if (focusBlock) {
      const { type } = focusBlock;
      focusBlockType = type;
    }

    const isFocusBlockComment =
      focusBlockType === showCommentCard.focusBlockType ||
      (focusBlockType &&
        focusBlockType.includes(showCommentCard.focusBlockType)) ||
      focusBlockType === showContextCard.focusBlockType ||
      (focusBlockType &&
        focusBlockType.includes(showContextCard.focusBlockType));

    return (
      <>
        {window.env !== 'test' && showSnackBar && (
          <SnackBarAlerts
            snackBarMessage={snackBarMessage}
            snackBarType={snackBarType}
            showSnackBar={showSnackBar}
            handleClose={this.handleClose}
          />
        )}
        <PersonalLinkWrapper>
          <PersonalHeader>
            <PersonalHeaderText>Personal Links</PersonalHeaderText>
            <PersonalHeaderIcon>
              <Tabs
                aria-label="Tabs sizes"
                size="sm"
                color="primary"
                variant="bordered"
                onSelectionChange={this.handleTabChange}
                classNames={{
                  tabList:
                    '!p-[3px] gap-0 border-[1px] border-solid border-[#E0E0E0] shadow-none',
                  tab: 'max-h-[16px] max-w-[40px]',
                }}
                defaultSelectedKey={this.getDefaultSelectedTab()}
              >
                <Tab key="icon" title="Icon" />
                <Tab key="link" title="Link" />
                <Tab key="text" title="Text" />
              </Tabs>
            </PersonalHeaderIcon>
          </PersonalHeader>
          <PersonalLinkEditor>
            <Editor
              ref={(editor) => {
                this.editorRef = editor;
              }}
              plugins={resumePlugins}
              schema={Schema.personalLinks}
              value={value}
              onChange={this.onChange}
              onBlur={this.onBlur}
              onPaste={this.onPaste}
              renderMark={this.renderMark}
              onKeyDown={this.onKeyDown}
              onFocus={this.onFocus}
              renderNode={this.renderBlock}
              className="editor" // when there are more drag elements in personal info, this has to be changed and change the droppable accordingly
            />
            {isFocusBlockComment && (
              <CommentsCard
                setSnackBarState={this.setSnackBarState}
                permission={permission}
                saveDocument={saveDocument}
                editorRef={this.editorRef}
                formatType={formatType}
              />
            )}
          </PersonalLinkEditor>
        </PersonalLinkWrapper>
      </>
    );
  }
}

PersonalLinks.propTypes = {
  personalLinks: propTypes.objectOf(propTypes.object).isRequired,
  updateSection: propTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { formatType } = ownProps;

  const common = {
    showCommentCard: state.commentsReducer.commentCard,
    showContextCard: state.commentsReducer.isContextMenu,
  };

  if (formatType === FormatTypes.RESUME) {
    return {
      personalLinks: state.editResumeFormReducer.personalLinks,
      userPreferences: state.editResumeFormReducer.userPreferences,
      covid19vaccinated:
        state.editResumeFormReducer.userPreferences.covid19vaccinated,
      showIcons: state.editResumeFormReducer.userPreferences.showIcons,
      showTexts: state.editResumeFormReducer.userPreferences.showTexts,
      showLinks: state.editResumeFormReducer.userPreferences.showLinks,
      ...common,
    };
  }

  if (formatType === FormatTypes.COVER_LETTER) {
    return {
      personalLinks: state.editCoverLetterFormReducer.personalLinks,
      userPreferences: state.editCoverLetterFormReducer.userPreferences,
      covid19vaccinated:
        state.editCoverLetterFormReducer.userPreferences.covid19vaccinated,
      showIcons: state.editCoverLetterFormReducer.userPreferences.showIcons,
      showTexts: state.editCoverLetterFormReducer.userPreferences.showTexts,
      showLinks: state.editCoverLetterFormReducer.userPreferences.showLinks,
      ...common,
    };
  }
  return null;
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { formatType } = ownProps;
  const common = {
    updatePopUpKeys: (params) => {
      dispatch(Actions.commentActions.updatePopUpKeys(params));
    },
    setMarkType: (params) =>
      dispatch(Actions.mostUsedToolsetActions.setMarkType(params)),
    updateUserPreferences: (params) =>
      dispatch(Actions.editDocumentFormActions.updateUserPreferences(params)),
  };

  if (formatType === FormatTypes.RESUME) {
    return {
      updateSection: (params) =>
        dispatch(Actions.editResumeFormActions.updateSection(params)),
      ...common,
    };
  }

  if (formatType === FormatTypes.COVER_LETTER) {
    return {
      updateSection: (params) =>
        dispatch(Actions.editCoverLetterFormActions.updateSection(params)),
      ...common,
    };
  }

  return null;
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonalLinks);
